import { Box, CircularProgress } from '@mui/material';
import HLButton from 'components/HLButton';
import HLDrawer from 'components/HLDrawer';
import { useHttp } from 'hooks/use-fetch';
import EditWaypoints from 'pages/Dashboard/EditWaypoints';
import { EDIT_WAYPOINT_SUGGESTIONS_VIEW } from 'pages/Dashboard/enums';
import { Waypoints } from 'pages/Dashboard/types';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { WaypointSuggestion } from 'pages/Dashboard/types/waypointSuggestion.types';
import React, { useState } from 'react';
import theme from 'theme';
import { MixpanelEventName } from 'utils/constants';
import { trackMixpanelEvent } from 'utils/utilMethods';

interface EditWaypointsButtonProps {
  patient: PatientObj;
  standardWaypoints: Waypoints[];
  patientWaypoints: Waypoints[];
  waypointSuggestions: WaypointSuggestion[];
  handleDeleteConfirmation: () => void;
  handleSendSuggestions: (suggestions: Partial<WaypointSuggestion>[]) => Promise<void>;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export default function EditWaypointsButton({
  patient,
  standardWaypoints,
  patientWaypoints,
  waypointSuggestions,
  handleDeleteConfirmation,
  handleSendSuggestions,
  isLoading = false,
  refetch,
}: EditWaypointsButtonProps) {
  const { isNewVersionEnabled } = useHttp();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenDrawer = () => {
    setIsOpen(true);
  };

  const handleCloseDrawer = () => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_EDIT_WAYPOINTS_PANEL_CLOSED, {
      location: 'progress_monitor_tab_activity_tracking_edit_waypoints_drawer',
    });
    refetch();
    setIsOpen(false);
  };

  const onEditWaypointsButtonClick = async () => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_EDIT_WAYPOINTS_BUTTON_CLICKED, {
      'patient has active waypoints': patientWaypoints.length > 0,
      yes: 'no',
      location: 'progress_monitor_tab_activity_tracking_edit_waypoints',
    });
    await refetch();
    handleOpenDrawer();
  };

  const activityText = isNewVersionEnabled ? 'Activities' : 'Waypoints';

  return (
    <>
      <HLButton variant='outlined' onClick={onEditWaypointsButtonClick} disabled={isLoading}>
        {patientWaypoints.length || waypointSuggestions.length ? 'EDIT' : 'SUGGEST'}
        {' '}
        {activityText.toUpperCase()}
        {isLoading && <CircularProgress sx={{ ml: 1 }} size={14} />}
      </HLButton>
      <HLDrawer
        title={`Edit ${activityText}`}
        open={isOpen}
        onClose={handleCloseDrawer}
        sx={{ zIndex: theme.zIndex.drawer + 2 }}
        maxWidth={780}
        data-testid='edit-waypoints-drawer'
      >
        <Box role='grid'>
          {isOpen && (
            <EditWaypoints
              view={
                waypointSuggestions.length
                  ? EDIT_WAYPOINT_SUGGESTIONS_VIEW.REVOKE_WAYPOINT_SUGGESTIONS
                  : EDIT_WAYPOINT_SUGGESTIONS_VIEW.SEND_WAYPOINT_SUGGESTIONS
              }
              patient={patient}
              standardWaypoints={standardWaypoints}
              patientWaypoints={patientWaypoints}
              waypointSuggestions={waypointSuggestions}
              handleOpenDrawer={handleOpenDrawer}
              handleCloseDrawer={handleCloseDrawer}
              handleDeleteConfirmation={handleDeleteConfirmation}
              handleSendSuggestions={handleSendSuggestions}
            />
          )}
        </Box>
      </HLDrawer>
    </>
  );
}
